import type { TFunction } from "i18next";
import { verificationCooldownUnlockDelay } from "./add-phone-wizard-state.js";

const SECOND_MS = 1_000;
const MINUTE_SECONDS = 60;
const HOUR_SECONDS = 60 * MINUTE_SECONDS;
const DAY_HOURS = 24;

export type VerificationCooldownSnapshot = {
  active: boolean;
  remainingMs: number;
};

/** Remaining wait derived only from the server deadline. */
export function verificationCooldownSnapshot(
  retryAfter: string | null,
  nowMs: number,
): VerificationCooldownSnapshot {
  const remainingMs = verificationCooldownUnlockDelay(retryAfter, nowMs);
  return remainingMs === null
    ? { active: false, remainingMs: 0 }
    : { active: true, remainingMs };
}

/**
 * Delay until the displayed whole second changes, at most one second.
 * Null once the deadline has passed so the ticker stops.
 */
export function verificationCooldownTickDelay(
  retryAfter: string | null,
  nowMs: number,
): number | null {
  const remainingMs = verificationCooldownUnlockDelay(retryAfter, nowMs);
  if (remainingMs === null) return null;
  return remainingMs % SECOND_MS || SECOND_MS;
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/** `mm:ss` under an hour, then hours and minutes, then days and hours. */
export function formatVerificationCountdown(t: TFunction, remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / SECOND_MS));
  if (totalSeconds < HOUR_SECONDS) {
    const minutes = Math.floor(totalSeconds / MINUTE_SECONDS);
    return `${pad2(minutes)}:${pad2(totalSeconds % MINUTE_SECONDS)}`;
  }
  const totalHours = Math.ceil(totalSeconds / HOUR_SECONDS);
  if (totalHours > DAY_HOURS) {
    return t("whatsapp.addPhone.cooldown.daysHours", {
      days: Math.floor(totalHours / DAY_HOURS),
      hours: totalHours % DAY_HOURS,
    });
  }
  const totalMinutes = Math.ceil(totalSeconds / MINUTE_SECONDS);
  return t("whatsapp.addPhone.cooldown.hoursMinutes", {
    hours: Math.floor(totalMinutes / MINUTE_SECONDS),
    minutes: totalMinutes % MINUTE_SECONDS,
  });
}

export function isLongVerificationCooldown(remainingMs: number): boolean {
  return remainingMs >= HOUR_SECONDS * SECOND_MS;
}
